import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export const EMPTY_PERIOD = { month: 'all', year: 'all' };

export const inPeriod = (iso, period) => {
  if (!period || (period.month === 'all' && period.year === 'all')) return true;
  if (!iso) return false;
  const d = new Date(iso);
  if (period.year !== 'all' && d.getFullYear() !== Number(period.year)) return false;
  if (period.month !== 'all' && d.getMonth() !== Number(period.month)) return false;
  return true;
};

export const yearsOf = (items, key = 'created_at') => {
  const ys = new Set(items.map((it) => (it[key] ? new Date(it[key]).getFullYear() : null)).filter(Boolean));
  ys.add(new Date().getFullYear());
  return Array.from(ys).sort((a, b) => b - a);
};

// Month/year filter + optional "delete closed" bulk action shared by every history list.
const PeriodFilter = ({ value, onChange, years, deletableCount = 0, deleteLabel = 'Borrar cerrados', onDeleteClosed, testPrefix = 'list' }) => {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const active = value.month !== 'all' || value.year !== 'all';

  const runDelete = async () => {
    setBusy(true);
    try { await onDeleteClosed(); } finally { setBusy(false); setConfirm(false); }
  };

  return (
    <div className="mb-4 flex flex-wrap items-end gap-2" data-testid={`${testPrefix}-period-filter`}>
      <div className="flex-1 sm:flex-none min-w-[120px] sm:min-w-[130px]">
        <Label className="text-xs text-slate-500">Mes</Label>
        <Select value={value.month} onValueChange={(v) => onChange({ ...value, month: v })}>
          <SelectTrigger data-testid={`${testPrefix}-filter-month`} className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los meses</SelectItem>
            {MONTHS.map((m, i) => <SelectItem key={m} value={String(i)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1 sm:flex-none min-w-[90px] sm:min-w-[110px]">
        <Label className="text-xs text-slate-500">Año</Label>
        <Select value={value.year} onValueChange={(v) => onChange({ ...value, year: v })}>
          <SelectTrigger data-testid={`${testPrefix}-filter-year`} className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {active && (
        <Button variant="outline" size="sm" data-testid={`${testPrefix}-period-clear`} onClick={() => onChange(EMPTY_PERIOD)}>
          Limpiar
        </Button>
      )}
      {onDeleteClosed && deletableCount > 0 && (
        <Button
          variant="outline"
          size="sm"
          data-testid={`${testPrefix}-delete-closed`}
          onClick={() => setConfirm(true)}
          className="ml-auto text-red-600 border-red-200 hover:bg-red-50"
        >
          <Trash2 size={14} className="mr-1" /> {deleteLabel} ({deletableCount})
        </Button>
      )}

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="max-w-sm" data-testid={`${testPrefix}-delete-dialog`}>
          <DialogHeader>
            <DialogTitle>¿Borrar {deletableCount} elemento{deletableCount === 1 ? '' : 's'}?</DialogTitle>
            <DialogDescription>
              Se eliminarán de tu historial los elementos cerrados que coinciden con el filtro actual. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" className="flex-1" onClick={() => setConfirm(false)}>Cancelar</Button>
            <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" disabled={busy} onClick={runDelete} data-testid={`${testPrefix}-delete-confirm`}>
              {busy ? 'Borrando…' : 'Sí, borrar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PeriodFilter;
